import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { organizerGuideUrl } from "./lib/api";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider, useLang } from "./context/LanguageContext";
import HomePage from "./pages/HomePage";
import EventDetailPage from "./pages/EventDetailPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import EventEditorPage from "./pages/EventEditorPage";
import AttendeesPage from "./pages/AttendeesPage";
import CheckinPage from "./pages/CheckinPage";
import OrganizerPage from "./pages/OrganizerPage";
import ForOrganizersPage from "./pages/ForOrganizersPage";
import PaymentPage from "./pages/PaymentPage";
import TeamPage from "./pages/TeamPage";
import AdminApprovalsPage from "./pages/AdminApprovalsPage";
import AdminInvoicesPage from "./pages/AdminInvoicesPage";
import AdminEventsPage from "./pages/AdminEventsPage";
import AdminEmailPage from "./pages/AdminEmailPage";
import AdminProspectsPage from "./pages/AdminProspectsPage";
import AdminVippsPage from "./pages/AdminVippsPage";
import AdminRepliesPage from "./pages/AdminRepliesPage";
import ChangelogPage from "./pages/ChangelogPage";
import NewsletterPage from "./pages/NewsletterPage";
import PaymentSettingsPage from "./pages/PaymentSettingsPage";
import OrganizerContactPage from "./pages/OrganizerContactPage";
import InvitePage from "./pages/InvitePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";
import HelpPage from "./pages/HelpPage";
import PricingPage from "./pages/PricingPage";
import ShopMarketplacePage from "./pages/ShopMarketplacePage";
import ShopOrganizerPage from "./pages/ShopOrganizerPage";
import ShopProductPage from "./pages/ShopProductPage";
import ShopOrderPage from "./pages/ShopOrderPage";
import ShopCheckoutPage from "./pages/ShopCheckoutPage";
import ShopDashboardPage from "./pages/ShopDashboardPage";
import ShopProductEditorPage from "./pages/ShopProductEditorPage";
import ShopOrdersPage from "./pages/ShopOrdersPage";
import ShopSettingsPage from "./pages/ShopSettingsPage";
import ShopStockCountPage from "./pages/ShopStockCountPage";
import BasarOverviewPage from "./pages/BasarOverviewPage";
import BasarPage from "./pages/BasarPage";
import BasarOrderPage from "./pages/BasarOrderPage";
import BasarDashboardPage from "./pages/BasarDashboardPage";
import BasarEditorPage from "./pages/BasarEditorPage";
import BasarAdminPage from "./pages/BasarAdminPage";
import BasarPrizesPage from "./pages/BasarPrizesPage";
import BasarSalesPage from "./pages/BasarSalesPage";
import BasarDrawPage from "./pages/BasarDrawPage";
import BasarCheckPage from "./pages/BasarCheckPage";
import BasarGiftPage from "./pages/BasarGiftPage";
import BasarGiftsPage from "./pages/BasarGiftsPage";
import VippsSimulatedPage from "./pages/VippsSimulatedPage";
import "./App.css";

const ProtectedRoute = ({ children }) => {
  const { organizer, loading } = useAuth();
  const { t } = useLang();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground text-sm">{t("Laster...")}</div>
      </div>
    );
  }
  if (!organizer) return <Navigate to="/logg-inn" replace />;
  return children;
};

// F1 hvor som helst på Right in: åpner «Så enkelt er det å komme i gang»-veiledningen (PDF) på valgt språk
const HelpShortcut = () => {
  const { lang } = useLang();
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "F1" || e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      window.open(organizerGuideUrl(lang), "_blank", "noopener,noreferrer");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lang]);
  return null;
};

function App() {
  return (
    <LanguageProvider>
    <HelpShortcut />
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/arrangement/:id" element={<EventDetailPage />} />
          <Route path="/arrangor/:id" element={<OrganizerPage />} />
          <Route path="/billetter/:orderId" element={<OrderConfirmationPage />} />
          <Route path="/betaling/:orderId" element={<PaymentPage />} />
          <Route path="/for-arrangorer" element={<ForOrganizersPage />} />
          <Route path="/logg-inn" element={<LoginPage />} />
          <Route path="/registrer" element={<RegisterPage />} />
          <Route path="/invitasjon/:code" element={<InvitePage />} />
          <Route path="/personvern" element={<PrivacyPage />} />
          <Route path="/vilkar" element={<TermsPage />} />
          <Route path="/om-oss" element={<AboutPage />} />
          <Route path="/hjelp" element={<HelpPage />} />
          <Route path="/pris" element={<PricingPage />} />
          <Route path="/nyheter" element={<ChangelogPage />} />
          <Route path="/nyhetsbrev" element={<NewsletterPage />} />
          {/* Butikk (tilleggsmodul) – offentleg. Spesifikke ruter før parameter-ruter. */}
          <Route path="/butikk" element={<ShopMarketplacePage />} />
          <Route path="/butikk/ordre/:orderId" element={<ShopOrderPage />} />
          <Route path="/butikk/:organizerId/kasse" element={<ShopCheckoutPage />} />
          <Route path="/butikk/:organizerId/vare/:productId" element={<ShopProductPage />} />
          <Route path="/butikk/:organizerId" element={<ShopOrganizerPage />} />
          {/* Basar (tilleggsmodul for loddsal) – offentleg. Spesifikke ruter før parameter-ruter. */}
          <Route path="/basar" element={<BasarOverviewPage />} />
          <Route path="/basar/ordre/:orderId" element={<BasarOrderPage />} />
          <Route path="/basar/gave/:giftId" element={<BasarGiftPage />} />
          <Route path="/basar/:id" element={<BasarPage />} />
          {/* Simulert Vipps-betaling (demo/førehandsvising – arrangør med env «simulated») */}
          <Route path="/vipps-simulert/:reference" element={<VippsSimulatedPage />} />
          {/* Basar – arrangørdashbord (spesifikke ruter før /:id) */}
          <Route path="/dashboard/basar" element={<ProtectedRoute><BasarDashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/ny" element={<ProtectedRoute><BasarEditorPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/oppsett" element={<ProtectedRoute><BasarEditorPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/premiar" element={<ProtectedRoute><BasarPrizesPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/gaver" element={<ProtectedRoute><BasarGiftsPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/sal" element={<ProtectedRoute><BasarSalesPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/trekning" element={<ProtectedRoute><BasarDrawPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/kontroll" element={<ProtectedRoute><BasarCheckPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id/kontroll/:orderId" element={<ProtectedRoute><BasarCheckPage /></ProtectedRoute>} />
          <Route path="/dashboard/basar/:id" element={<ProtectedRoute><BasarAdminPage /></ProtectedRoute>} />
          {/* Butikk – arrangørdashbord */}
          <Route path="/dashboard/butikk" element={<ProtectedRoute><ShopDashboardPage /></ProtectedRoute>} />
          <Route path="/dashboard/butikk/ny" element={<ProtectedRoute><ShopProductEditorPage /></ProtectedRoute>} />
          <Route path="/dashboard/butikk/vare/:id" element={<ProtectedRoute><ShopProductEditorPage /></ProtectedRoute>} />
          <Route path="/dashboard/butikk/ordrer" element={<ProtectedRoute><ShopOrdersPage /></ProtectedRoute>} />
          <Route path="/dashboard/butikk/innstillinger" element={<ProtectedRoute><ShopSettingsPage /></ProtectedRoute>} />
          <Route path="/dashboard/butikk/lagerteljing" element={<ProtectedRoute><ShopStockCountPage /></ProtectedRoute>} />
          <Route
            path="/dashboard/team"
            element={
              <ProtectedRoute>
                <TeamPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/godkjenninger"
            element={
              <ProtectedRoute>
                <AdminApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/fakturaer-admin"
            element={
              <ProtectedRoute>
                <AdminInvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/arrangementer-admin"
            element={
              <ProtectedRoute>
                <AdminEventsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/epost-admin"
            element={
              <ProtectedRoute>
                <AdminEmailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kontaktregister-admin"
            element={
              <ProtectedRoute>
                <AdminProspectsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/vipps-admin"
            element={
              <ProtectedRoute>
                <AdminVippsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/svar"
            element={
              <ProtectedRoute>
                <AdminRepliesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/betaling"
            element={
              <ProtectedRoute>
                <PaymentSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/kontaktinfo"
            element={
              <ProtectedRoute>
                <OrganizerContactPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/ny"
            element={
              <ProtectedRoute>
                <EventEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/rediger/:id"
            element={
              <ProtectedRoute>
                <EventEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/deltakere/:id"
            element={
              <ProtectedRoute>
                <AttendeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/sjekk-inn/:id"
            element={
              <ProtectedRoute>
                <CheckinPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
